import type React from 'react';
import { useState } from 'react';
import { useAuth } from '@providers';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@components/ui/drawer';
import { FilterChips, type FilterChipItem } from '@components/ui/FilterChips';
import { UserAvatar } from '@components/UserAvatar';
import { usePollVoters } from '@hooks/usePollVoters';
import { tryLoadAgain } from '@lib/tryLoadAgain';
import { cn } from '@lib/utils';
import type { PollOptionRead } from '@models/api/poll';
import type { SpaceUserSummary } from '@models/api/space';
import { POLL_COPY } from './pollCopy';

export interface PollVotersDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  questionId: string;
  /** The poll's question, under the title. */
  question: string;
  options: PollOptionRead[];
  /** Only while `can_see_voters`: the list route refuses everyone else. */
  canSeeVoters: boolean;
}

/** Most answers first (polls-B6 "Order by count, highest first"); a tie keeps the poll's order. */
const byCount = (options: PollOptionRead[]): PollOptionRead[] =>
  [...options].sort(
    (a, b) => (b.votes_count ?? 0) - (a.votes_count ?? 0) || a.position - b.position,
  );

const LIST_BUTTON =
  'mt-3 min-h-11 w-full rounded-xl text-sm font-medium text-mb-teal-ink hover:bg-slate-50 disabled:opacity-60';

/**
 * "Who answered" (TECH-197 B6, from "{n} people answered · See who"): the question, one chip per
 * option "{option} · {count}", most answers first, and the people who picked the chosen one, the
 * viewer marked "(you)". A long option ends with "…" and keeps its full text as the chip's name
 * (RESOLUTIONS P11). The app's own dimmed backdrop (P1).
 */
export const PollVotersDrawer: React.FC<PollVotersDrawerProps> = ({
  open,
  onOpenChange,
  questionId,
  question,
  options,
  canSeeVoters,
}) => (
  <Drawer open={open} onOpenChange={onOpenChange}>
    <DrawerContent className="max-h-[85dvh] border-x-0 border-b-0 border-slate-200 pb-6">
      {/* Mounted while open only: each opening starts on the top chip. */}
      {open && (
        <VotersSheetBody
          questionId={questionId}
          question={question}
          options={options}
          canSeeVoters={canSeeVoters}
        />
      )}
    </DrawerContent>
  </Drawer>
);

const VotersSheetBody: React.FC<Omit<PollVotersDrawerProps, 'open' | 'onOpenChange'>> = ({
  questionId,
  question,
  options,
  canSeeVoters,
}) => {
  const ordered = byCount(options);
  const [picked, setPicked] = useState<string | null>(null);
  // The chosen chip; the top one until another is tapped (a poll has 2 to 10 options).
  const selectedId: string = picked ?? ordered.at(0)?.id ?? '';
  const voters = usePollVoters({
    questionId,
    optionId: selectedId || null,
    enabled: canSeeVoters,
  });

  const chips: FilterChipItem[] = ordered.map(option => {
    const count = option.votes_count ?? 0;
    // The option truncates with "…"; its count stays whole after it (FE-1c review).
    return {
      value: option.id,
      label: option.text,
      trailing: POLL_COPY.optionChipCount(count),
      accessibleLabel: POLL_COPY.optionChip(option.text, count),
    };
  });

  return (
    <>
      <DrawerHeader className="gap-1 px-4 pt-4 pb-3 text-left">
        <DrawerTitle className="text-lg leading-[22px] font-semibold tracking-normal text-mb-ink">
          {POLL_COPY.whoAnswered}
        </DrawerTitle>
        <DrawerDescription dir="auto" className="text-sm leading-[18px] text-mb-dark-gray">
          {question}
        </DrawerDescription>
      </DrawerHeader>
      <div className="shrink-0 px-4">
        <FilterChips
          items={chips}
          value={selectedId}
          onChange={setPicked}
          ariaLabel={POLL_COPY.whoAnswered}
          density="inline"
          appearance="outline"
        />
      </div>
      <div className="min-h-0 overflow-y-auto px-4 pt-1.5">
        <VotersList voters={voters} />
      </div>
    </>
  );
};

const VotersList: React.FC<{ voters: ReturnType<typeof usePollVoters> }> = ({ voters }) => {
  const { user } = useAuth();
  const viewerId = user?.id;

  if (voters.isLoading) {
    return (
      <ul aria-busy="true" data-testid="poll-voters-loading">
        {[0, 1, 2].map(index => (
          <li key={index} className="flex items-center gap-3 py-2.5">
            <span className="size-10 shrink-0 animate-pulse rounded-full bg-slate-100" />
            <span className="h-4 w-32 animate-pulse rounded bg-slate-100" />
          </li>
        ))}
      </ul>
    );
  }
  if (voters.isError && voters.voters.length === 0) {
    return (
      <button
        type="button"
        className={LIST_BUTTON}
        onClick={() => {
          tryLoadAgain(voters.refetch);
        }}
      >
        {POLL_COPY.loadFailed}
      </button>
    );
  }
  if (voters.voters.length === 0) {
    return <p className="py-2.5 text-sm leading-5 text-mb-dark-gray">{POLL_COPY.noOneAnswered}</p>;
  }
  return (
    <>
      <ul data-testid="poll-voters-list">
        {voters.voters.map(voter => (
          <VoterRow key={voter.id} voter={voter} isViewer={voter.id === viewerId} />
        ))}
      </ul>
      {(voters.hasNextPage || voters.isFetchNextPageError) && (
        <button
          type="button"
          className={LIST_BUTTON}
          disabled={voters.isFetchingNextPage}
          onClick={() => {
            // One button, two jobs: after a failed page it is a retry.
            if (voters.isFetchNextPageError) tryLoadAgain(voters.fetchNextPage);
            else void voters.fetchNextPage();
          }}
        >
          {voters.isFetchingNextPage
            ? POLL_COPY.loadingMore
            : voters.isFetchNextPageError
              ? POLL_COPY.loadMoreFailed
              : POLL_COPY.loadMore}
        </button>
      )}
    </>
  );
};

const VoterRow: React.FC<{ voter: SpaceUserSummary; isViewer: boolean }> = ({
  voter,
  isViewer,
}) => {
  const name = voter.full_name ?? '';
  return (
    <li
      className={cn('flex items-center gap-3 border-b border-slate-100 py-2.5 last:border-b-0')}
      data-testid="poll-voter-row"
    >
      <UserAvatar imageUrl={voter.profile_picture} fullName={name} size="md" className="shrink-0" />
      <span
        dir="auto"
        className="min-w-0 truncate text-[15px] leading-[18px] font-medium text-mb-ink"
      >
        {isViewer ? `${name} ${POLL_COPY.you}` : name}
      </span>
    </li>
  );
};
